import type { SvgHaptoLabel, SvgIonicLink, SvgLineSegment, SvgStereoWedge } from './drawingPaperRenderModel';
import { ionicBoltPath, wavyBondPath } from '../rendering/bondMarks';

/** #868 -- a bond's own colour and on-screen width, when it has an authored appearance. */
export type BondLook = (bondId: string) => { readonly color?: string; readonly strokeWidth?: number } | undefined;

export function PaperBondGeometry({
  svgLines,
  stereoWedges,
  bondStrokeWidth,
  bondLook,
  viewportZoom = 1,
  ionicLinks = [],
  shownIonicIds,
  haptoLabels = [],
  labelFontSize = 12
}: {
  svgLines: readonly SvgLineSegment[];
  stereoWedges: readonly SvgStereoWedge[];
  bondStrokeWidth: number;
  viewportZoom?: number;
  bondLook?: BondLook | undefined;
  // ADR 0022 (as amended) -- the bolts between a salt's ions, and which of them show (hovered,
  // an ion selected, or all with "Show ionic bonds"); the rest draw nothing, as IUPAC asks.
  ionicLinks?: readonly SvgIonicLink[] | undefined;
  shownIonicIds?: ReadonlySet<string> | undefined;
  // ADR 0026 D2 -- η labels, with "Show hapticity" on.
  haptoLabels?: readonly SvgHaptoLabel[] | undefined;
  labelFontSize?: number | undefined;
}) {
  const lineStyle = (bondId: string, strokeScale = 1) => {
    const look = bondLook?.(bondId);
    return {
      strokeWidth: (look?.strokeWidth ?? bondStrokeWidth) * strokeScale,
      ...(look?.color ? { stroke: look.color } : {})
    };
  };
  const solidWedgePoints = (wedge: SvgStereoWedge) => {
    // Lines use non-scaling strokes; convert their screen width to SVG coordinates.
    const half = lineStyle(wedge.id).strokeWidth / (2 * viewportZoom);
    return `${wedge.from.x - wedge.nx * half},${wedge.from.y - wedge.ny * half} ${wedge.from.x + wedge.nx * half},${wedge.from.y + wedge.ny * half} ${wedge.to.x + wedge.nx * wedge.halfWidth},${wedge.to.y + wedge.ny * wedge.halfWidth} ${wedge.to.x - wedge.nx * wedge.halfWidth},${wedge.to.y - wedge.ny * wedge.halfWidth}`;
  };
  return (
    <>
      {ionicLinks
        .filter((link) => shownIonicIds?.has(link.id))
        .map((link) => (
          <path
            key={link.id}
            className="ionic-bond-bolt"
            d={ionicBoltPath(link.x1, link.y1, link.x2, link.y2, link.width)}
            style={{ strokeWidth: bondStrokeWidth }}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      {haptoLabels.map((label) => (
        <text
          key={label.id}
          className="hapto-label"
          x={label.x}
          y={label.y}
          fontSize={labelFontSize}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {label.text}
        </text>
      ))}
      {svgLines.map((line) =>
        line.kind === 'wavy' ? (
          <path
            key={line.id}
            className="wavy-bond"
            d={wavyBondPath(line.x1, line.y1, line.x2, line.y2, line.markSize ?? 3)}
            style={lineStyle(line.bondId)}
            vectorEffect="non-scaling-stroke"
          />
        ) : (
          <line
            key={line.id}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            style={lineStyle(line.bondId, line.strokeScale)}
            className={
              line.kind === 'hydrogen'
                ? 'hydrogen-bond'
                : line.aromaticInner
                  ? 'aromatic-inner-bond'
                  : line.aromatic
                    ? 'aromatic-bond'
                    : undefined
            }
            markerEnd={
              line.dativeHead ? 'url(#arrowhead-full)' : line.kind === 'agostic' ? 'url(#arrowhead-half)' : undefined
            }
            vectorEffect="non-scaling-stroke"
          />
        )
      )}
      {stereoWedges.map((wedge) =>
        wedge.style === 'solid' ? (
          <polygon
            key={wedge.id}
            style={bondLook?.(wedge.id)?.color ? { fill: bondLook(wedge.id)!.color } : undefined}
            className="stereo-solid-wedge"
            points={solidWedgePoints(wedge)}
          />
        ) : (
          <g
            key={wedge.id}
            className="stereo-hashed-wedge"
            style={bondLook?.(wedge.id)?.color ? { stroke: bondLook(wedge.id)!.color } : undefined}
          >
            {[0.18, 0.34, 0.5, 0.66, 0.82, 1].map((fraction) => {
              const x = wedge.from.x + (wedge.to.x - wedge.from.x) * fraction;
              const y = wedge.from.y + (wedge.to.y - wedge.from.y) * fraction;
              const half = wedge.halfWidth * fraction;
              return (
                <line
                  key={fraction}
                  x1={x - wedge.nx * half}
                  y1={y - wedge.ny * half}
                  x2={x + wedge.nx * half}
                  y2={y + wedge.ny * half}
                  style={{ strokeWidth: lineStyle(wedge.id).strokeWidth }}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </g>
        )
      )}
    </>
  );
}
